import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  getUserById,
  updateUser,
  deleteUser,
  setUserLicense,
  creditUser,
  suspendUser,
  activateUser,
  impersonateUser,
  resetUserPassword,
  AdminLicenseSelection,
} from '../../services/adminService';
import { useAuth } from '../../contexts/AuthContext';
import {
  ChevronLeft,
  Edit2,
  Save,
  X,
  Trash2,
  RefreshCw,
  Clock,
  LogIn,
  AlertTriangle,
  Loader2,
  Lock,
  Ban,
  CheckCircle,
} from 'lucide-react';

interface UserDetails {
  user: {
    id: string;
    email: string;
    fullName: string;
    companyName: string;
    accountType: string;
    license?: AdminLicenseSelection | 'free' | 'commercial' | 'template_pack' | 'professional' | 'agency';
    creditsBalance?: number;
    role: string;
    subscriptionStatus: string;
    subscriptionExpiresAt: string;
    monthlyLimit: number;
    generationsThisMonth: number;
    avatarUrl: string;
    createdAt: string;
    updatedAt: string;
    lastSignInAt: string;
  };
  statistics: {
    totalGenerations: number;
    last30Days: number;
    templateUsage: Record<string, number>;
    averagePerDay: number;
  };
  recentMockups: any[];
  recentActivity: any[];
}

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    fullName: '',
    companyName: '',
  });
  const [licenseForm, setLicenseForm] = useState<AdminLicenseSelection>('free');
  const [creditAmount, setCreditAmount] = useState<number>(100);
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Password Reset
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  // Impersonation
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [impersonateError, setImpersonateError] = useState<string | null>(null);

  const handleResetPassword = async () => {
    if (!id || !newPassword) return;
    if (newPassword.length < 6) {
        alert('Password must be at least 6 characters long');
        return;
    }
    
    setResetPasswordLoading(true);
    try {
      await resetUserPassword(id, newPassword);
      alert('Password reset successfully!');
      setShowPasswordReset(false);
      setNewPassword('');
    } catch (err: any) {
      console.error('Error resetting password:', err);
      alert(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadUserDetails();
    }
  }, [id]);

  const loadUserDetails = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await getUserById(id);
      setUserDetails(data);
      setEditForm({
        fullName: data.user.fullName || '',
        companyName: data.user.companyName || '',
      });
      setLicenseForm((data.user.license as any) || 'free');
      console.log(`Loaded details for user ${id} [[memory:5998053]]`);
    } catch (err) {
      console.error('Error loading user details:', err);
      setError('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!id) return;

    try {
      await updateUser(id, {
        full_name: editForm.fullName,
        company_name: editForm.companyName,
      });
      setIsEditing(false);
      await loadUserDetails();
    } catch (err) {
      console.error('Error updating user:', err);
      alert('Failed to update user');
    }
  };

  const handleSuspend = async () => {
    if (!id) return;
    if (!window.confirm('Suspend this user? They will be blocked from using the app until reactivated.')) return;
    try {
      setStatusUpdating(true);
      await suspendUser(id, 'Admin suspended');
      await loadUserDetails();
    } catch (err) {
      console.error('Error suspending user:', err);
      alert('Failed to suspend user');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleActivate = async () => {
    if (!id) return;
    try {
      setStatusUpdating(true);
      await activateUser(id);
      await loadUserDetails();
    } catch (err) {
      console.error('Error activating user:', err);
      alert('Failed to activate user');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    
    try {
      await deleteUser(id);
      console.log(`Deleted user ${id} [[memory:5998053]]`);
      navigate('/admin/users');
    } catch (err) {
      console.error('Error deleting user:', err);
      alert('Failed to delete user');
    }
  };

  const handleImpersonate = async () => {
    if (!id) return;
    setShowImpersonateModal(true);
    setImpersonateError(null);
  };

  const handleStartImpersonation = async () => {
    if (!id || !userDetails) {
      return;
    }

    setImpersonating(true);
    setImpersonateError(null);

    try {
      const data = await impersonateUser(id);

      if (data.accessToken && data.refreshToken) {
        await startImpersonation({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          targetUserId: data.user?.id || userDetails.user.id,
          targetEmail: data.user?.email || userDetails.user.email,
          targetName: data.user?.fullName || userDetails.user.fullName || userDetails.user.email,
          adminReturnPath: `${location.pathname}${location.search}`,
        });

        window.location.assign('/dashboard');
        return;
      }

      if (data.url) {
        window.location.assign(data.url);
        return;
      }

      throw new Error('Impersonation response did not include a usable session');
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to log in as this user';
      setImpersonateError(msg);
    } finally {
      setImpersonating(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (error || !userDetails) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'User not found'}</p>
          <Link to="/admin/users" className="text-indigo-600 hover:text-indigo-900">
            Back to Users
          </Link>
        </div>
      </div>
    );
  }

  const { user, statistics, recentMockups, recentActivity } = userDetails;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb + Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <nav className="flex items-center text-sm text-gray-500 mb-1">
            <Link to="/admin/users" className="hover:text-indigo-600">Users</Link>
            <ChevronLeft className="h-3 w-3 mx-1 rotate-180" />
            <span className="text-gray-900">{user.fullName || user.email}</span>
          </nav>
          <h1 className="text-2xl font-bold text-gray-900">User Details</h1>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleImpersonate}
            className="inline-flex items-center px-4 py-2 border border-amber-300 rounded-md shadow-sm text-sm font-medium text-amber-700 bg-white hover:bg-amber-50"
          >
            <LogIn className="-ml-1 mr-2 h-4 w-4" />
            Login as User
          </button>
          <button
            onClick={handleDeleteUser}
            className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50"
          >
            <Trash2 className="-ml-1 mr-2 h-4 w-4" />
            Delete User
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* User Profile */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Profile Information</h3>
                  {!isEditing ? (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                  ) : (
                    <div className="flex space-x-2">
                      <button
                        onClick={handleUpdateUser}
                        className="text-green-600 hover:text-green-900"
                      >
                        <Save className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setIsEditing(false);
                          setEditForm({
                            fullName: user.fullName || '',
                            companyName: user.companyName || '',
                          });
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Full Name</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.fullName}
                        onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">{user.fullName || 'Not provided'}</p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <p className="mt-1 text-sm text-gray-900">{user.email}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500">Company</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.companyName}
                        onChange={(e) => setEditForm({ ...editForm, companyName: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">{user.companyName || 'Not provided'}</p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500">Role</label>
                    <p className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.role === 'super_admin' ? 'bg-red-100 text-red-800' :
                        user.role === 'admin' ? 'bg-orange-100 text-orange-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {user.role}
                      </span>
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500">Created</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(user.createdAt)}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500">Last Sign In</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDateTime(user.lastSignInAt)}</p>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <label className="text-sm font-medium text-gray-500 mb-2 block">Security</label>
                    {!showPasswordReset ? (
                      <button
                        onClick={() => setShowPasswordReset(true)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <Lock className="-ml-0.5 mr-2 h-4 w-4 text-gray-400" />
                        Reset User Password
                      </button>
                    ) : (
                      <div className="flex flex-col space-y-2">
                        <input
                          type="text"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Enter new password"
                          className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={handleResetPassword}
                            disabled={resetPasswordLoading}
                            className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                          >
                            {resetPasswordLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                            Confirm Reset
                          </button>
                          <button
                            onClick={() => {
                              setShowPasswordReset(false);
                              setNewPassword('');
                            }}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Account Status */}
            <div className="bg-white shadow rounded-lg mt-6">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Account Status</h3>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Current Status</label>
                    <p className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.subscriptionStatus === 'active' ? 'bg-green-100 text-green-800' :
                        user.subscriptionStatus === 'suspended' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {user.subscriptionStatus || 'active'}
                      </span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Suspended users are blocked from signing in and using the app.
                    </p>
                  </div>

                  {user.subscriptionStatus === 'suspended' ? (
                    <button
                      onClick={handleActivate}
                      disabled={statusUpdating}
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                    >
                      {statusUpdating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                      Reactivate Account
                    </button>
                  ) : (
                    <button
                      onClick={handleSuspend}
                      disabled={statusUpdating}
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                    >
                      {statusUpdating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Ban className="h-4 w-4 mr-2" />}
                      Suspend Account
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* License & Credits */}
            <div className="bg-white shadow rounded-lg mt-6">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">License & Credits</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">License</label>
                    <select
                      value={licenseForm}
                      onChange={(e) => setLicenseForm(e.target.value as any)}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value="free">Free (+10)</option>
                      <option value="commercial">Commercial (+275)</option>
                      <option value="template_pack">Commercial + Template Pack (+775)</option>
                      <option value="professional">Commercial + Professional (+1,000)</option>
                      <option value="agency">Commercial + Agency (+1,375)</option>
                      <option value="reseller">Commercial + Reseller (+275)</option>
                      <option value="bundle">Bundle (+2,600)</option>
                      <option value="fastpass">Commercial + Fastpass (+2,600)</option>
                    </select>
                    <button
                      onClick={async () => {
                        if (!id) return;
                        try {
                          await setUserLicense(id, licenseForm);
                          await loadUserDetails();
                          alert('License updated successfully');
                        } catch (err: any) {
                          console.error('Error updating license:', err);
                          alert(err.response?.data?.error || 'Failed to update license');
                        }
                      }}
                      className="mt-3 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Apply License & Grant Credits
                    </button>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <label className="text-sm font-medium text-gray-500">Add Bonus Credits</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={creditAmount}
                        onChange={(e) => setCreditAmount(parseInt(e.target.value || '0', 10))}
                        className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm w-40"
                      />
                      <button
                        onClick={async () => {
                          if (!id) return;
                          try {
                            await creditUser(id, creditAmount, 'admin_bonus');
                            await loadUserDetails();
                            alert('Bonus credits added successfully');
                          } catch (err: any) {
                            console.error('Error crediting user:', err);
                            alert(err.response?.data?.error || 'Failed to add bonus credits');
                          }
                        }}
                        className="inline-flex items-center px-3 py-2 border border-blue-300 shadow-sm text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100"
                      >
                        Add Credits
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Current balance: {userDetails?.user?.creditsBalance ?? '—'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Statistics and Activity */}
          <div className="lg:col-span-2 space-y-6">
            {/* Usage Statistics */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Usage Statistics</h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-500">Total Generations</p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900">
                      {statistics.totalGenerations}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-500">Last 30 Days</p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900">
                      {statistics.last30Days}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-500">Credits Balance</p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900">
                      {user.creditsBalance ?? 0}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-500">Avg per Day (30d)</p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900">
                      {statistics.averagePerDay.toFixed(1)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Template Usage */}
            {Object.keys(statistics.templateUsage).length > 0 && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Template Usage</h3>
                  <div className="space-y-2">
                    {Object.entries(statistics.templateUsage)
                      .sort(([, a], [, b]) => b - a)
                      .map(([template, count]) => (
                        <div key={template} className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">{template}</span>
                          <span className="text-sm font-medium text-gray-900">{count}</span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}

            {/* Recent Mockups */}
            {recentMockups.length > 0 && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Mockups</h3>
                  <div className="space-y-3">
                    {recentMockups.slice(0, 5).map((mockup) => (
                      <div key={mockup.id} className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{mockup.name}</p>
                          <p className="text-xs text-gray-500">
                            {formatDateTime(mockup.created_at)}
                          </p>
                        </div>
                        <span className="text-xs text-gray-600">
                          {mockup.downloads} downloads
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Recent Activity */}
            {recentActivity.length > 0 && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
                  <div className="space-y-3">
                    {recentActivity.slice(0, 10).map((activity) => (
                      <div key={activity.id} className="flex items-start space-x-3">
                        <Clock className="h-4 w-4 text-gray-400 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-gray-900">
                            {activity.activity_type.replace(/_/g, ' ')}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDateTime(activity.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

      {/* Impersonate User Modal */}
      {showImpersonateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowImpersonateModal(false)} />
            <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6 z-10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <LogIn className="h-5 w-5 text-amber-600" />
                  Login as User
                </h3>
                <button onClick={() => setShowImpersonateModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">You are about to enter this user account.</p>
                  <p className="mt-1 text-xs">Your admin session will be backed up so you can return to the admin panel from inside the user dashboard.</p>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Target: <span className="font-medium text-gray-900">{user.fullName || user.email}</span>
                </p>
                <p className="text-sm text-gray-500">{user.email}</p>
              </div>

              {impersonating && (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-6 w-6 text-amber-600 animate-spin" />
                  <span className="ml-2 text-sm text-gray-600">Switching into the user account...</span>
                </div>
              )}

              {impersonateError && (
                <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                  {impersonateError}
                </div>
              )}

              <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-100">
                <button
                  onClick={handleStartImpersonation}
                  disabled={impersonating}
                  className="px-4 py-2 text-sm font-medium text-white bg-amber-600 border border-transparent rounded-lg hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Enter User Account
                </button>
                <button
                  onClick={() => setShowImpersonateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetail;
